import { Signal } from 'openfin-service-signal';
import { injectable, inject } from 'inversify';
import { Rect, MonitorInfo } from 'openfin/_v2/api/system/monitor';
import { Action } from '@openfin/predux';

import { Inject } from '../common/Injectables';
import { MonitorModel } from '../model/MonitorModel';
import { Debounce } from '../utils/Debounce';
import { ServiceStore } from '../store/ServiceStore';
import { SetToastPosition } from '../store/partials/Settings';
import { RootState } from '../store/State';
import { clamp } from '../utils';

import { Rectangle, Point, LayoutableWindowState, ReadonlyRectangle } from './LayoutableWindow';
import { AsyncInit } from './AsyncInit';
import { LayouterConfig } from './LayouterConfig';
import { LayoutStack } from './LayoutStack';

// Mac os freezes when the window width/height is set to 0
let MIN_WINDOW_HEIGHT = 10;

export interface LayoutItem {
  readonly state: LayoutableWindowState;
  readonly size: Readonly<Point>;
  readonly maxSize: Readonly<Point>;

  setState(state: LayoutableWindowState): Promise<void>;
  setTransform(transform: ReadonlyRectangle): Promise<void>;
  animate(bounds: ReadonlyRectangle, config: LayouterConfig): Promise<void>;
}

@injectable()
export class Layouter extends AsyncInit {
  public onLayoutRequired: Signal<[]> = new Signal();
  private readonly _layoutDebounce = new Debounce();

  private readonly _monitorModel: MonitorModel;
  private _availableRect!: ReadonlyRectangle;
  private _spawnPosition!: Point;
  private _config!: LayouterConfig;
  private _offset = 0;

  public layoutPromise: Promise<void> | null = null;

  constructor(
    @inject(Inject.MONITOR_MODEL) monitorModel: MonitorModel,
    @inject(Inject.STORE) private readonly store: ServiceStore
  ) {
    super();

    this._monitorModel = monitorModel;

    this.store.onAction.add(this.onAction, this);
  }

  public async init(): Promise<void> {
    await this._monitorModel.initialized;
    await this.store.initialized;
    this._config = this.createConfig();

    this.onMonitorChange(this._monitorModel.monitorInfo.primaryMonitor.availableRect);

    this._monitorModel.onMonitorInfoChanged.add((monitorInfo: MonitorInfo) => {
      this.onMonitorChange(monitorInfo.primaryMonitor.availableRect);
      this.onLayoutRequired.emit();
    });

    const hostSpecs = await fin.System.getHostSpecs();
    const name = hostSpecs.name.toLowerCase();
    if (name.includes('windows')) {
      MIN_WINDOW_HEIGHT = 0;
    }
  }

  private createConfig() {
    const x = this.store.state.settings.toastPosition.includes('left') ? -1 : 1;
    const y = this.store.state.settings.toastPosition.includes('top') ? -1 : 1;
    const config = new LayouterConfig({ x, y });
    console.log('Config created', config);
    return config;
  }

  private async onAction(action: Action<RootState>): Promise<void> {
    if (action instanceof SetToastPosition) {
      this._config = this.createConfig();
      this.updateSpawnPosition();
      this.onLayoutRequired.emit();
    }
  }

  /**
   * Layout a given stack of Layoutable items. Toast positions are calculated and assigned synchronously.
   *
   * @param stack Target layoutable item stack
   */
  public layout(stack: LayoutStack): void {
    const promise = new Promise<void>((resolve) => {
      const fn = async () => {
        const { spacing } = this._config;
        const promises: Promise<void>[] = [];
        let currentOffset = 0;

        for (const item of stack.items) {
          if (item.state >= LayoutableWindowState.ACTIVE) {
            const transform: Rectangle = this.calculateItemBounds(item, currentOffset);
            promises.push(item.animate(transform, this._config));

            // Only add spacing between elements if item has non-zero height
            currentOffset += transform.size.y + spacing * Math.sign(transform.size.y);
          } else {
            // Should never happen, toasts should remain in queue until they are initialised
            console.warn(`An uninitialised toast is in the stack: ${item.id}`);
          }
        }

        this._offset = currentOffset;
        stack.updateHeight(currentOffset);
        this.layoutPromise = null;
        resolve();
      };

      this._layoutDebounce.invoke(fn);
    });
    this.layoutPromise = promise;
  }

  /**
   * Moves and resizes the window to its initial position after its spawn
   * @param item Target layoutable item
   */
  public async setInitialTransform(item: LayoutItem): Promise<void> {
    return item.setTransform(this.calculateItemBounds(item, this._offset));
  }

  /**
   * Checks if item would fit in screen fully if it was added to the given stack and laid out.
   *
   * The items returned should be removed from the queue and added to the stack.
   *
   * @param stack Current stack/queue state
   * @param queuedItems Items waiting to be queued. (typically, `stack.queue`)
   */
  public getFittingItems(stack: LayoutStack, queuedItems: ReadonlyArray<LayoutItem>): LayoutItem[] {
    const { spacing } = this._config;
    const fittingItems: LayoutItem[] = [];
    const queue: LayoutItem[] = queuedItems.slice();
    let availableHeight: number = this._availableRect.size.y - stack.height;

    for (
      let i = 0, length = queue.length;
      i < length && queue[i].state >= LayoutableWindowState.QUEUED;
      i++
    ) {
      const height = queue[i].size.y;

      if (height + spacing < availableHeight) {
        availableHeight -= height + spacing;
        fittingItems.push(queue[i]);
      } else {
        break;
      }
    }

    return fittingItems;
  }

  private updateSpawnPosition(): void {
    const { anchor, spacing, origin } = this._config;
    const { origin: monitorOrigin, size: monitorSize } = this._availableRect;
    this._spawnPosition = {
      x: Math.floor(monitorOrigin.x + monitorSize.x * anchor.x - spacing * origin.x),
      y: Math.floor(monitorOrigin.y + monitorSize.y * anchor.y - spacing * origin.y),
    };
  }

  private onMonitorChange(available: Rect): void {
    this._availableRect = {
      origin: { x: available.left, y: available.top },
      size: {
        x: Math.floor(available.right - available.left),
        y: Math.floor(available.bottom - available.top),
      },
    };

    this.updateSpawnPosition();
  }

  private calculateItemBounds(item: LayoutItem, offset: number): Rectangle {
    const { anchor, direction } = this._config;
    const spawnPos: Point = this._spawnPosition;

    const size: Point = {
      x: clamp(item.size.x, item.maxSize.x, item.maxSize.x) | 0,
      y: item.state === LayoutableWindowState.ACTIVE ? item.size.y : MIN_WINDOW_HEIGHT,
    };
    const origin: Point = {
      x:
        spawnPos.x -
        (size.x + (this._config.toastWidth - size.x)) * anchor.x +
        offset * direction.x,
      y: spawnPos.y - size.y * anchor.y + offset * direction.y,
    };

    return { origin, size };
  }
}
